// store.ts
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadState, saveState } from './localStorage';
import {
    ChatInteraction,
    ChatItem,
    ChatElement,
    ChatSegmentRequest,
    ContextItem,
} from '../Service/ServerAPI';
import { DatasetInfo } from '../types/types';

import { TabValue } from '@fluentui/react-components';

// Define the options (since options are just data, it's okay to define them here)
const options = [
    { key: 'title', text: 'Game/App' },
    { key: 'franchise', text: 'Franchise' },
    { key: 'series', text: 'Series' },
    { key: 'products', text: 'Products' },
    { key: 'subscriptions', text: 'Subscriptions' },
    { key: 'semantic', text: 'Semantic Search' },
];

type EvaluationFeedback = {
    evaluationType: 'unsure' | 'mismatch' | 'info';
    message: string;
    timestamp: number;
    sessionId: string;
};

type SettingsState = {
    apiEndpoint: string;
    name: string;
    pseudocodeChat: ChatItem[];
    chat: ChatElement[];
    isProcessing: boolean;
    recentReasoningNotifications: string[];
    processNotificationIndex: number | null; // Track single process notification index
    sessionId: string;
    sessionOwner: string;
    isViewingOthersSession: boolean;
    activeSessionId: string;
    context: { [key: string]: ContextItem };
    isDialogOpen: boolean;
    dialogSearchString: string;
    searchTabString: TabValue;
    useChatv2: boolean;
    enableExportFeature: boolean;
    enableEvaluation: boolean;
    evaluationFeedback: EvaluationFeedback[];
    dataset: string; // Global toggle — which dataset to use for new sessions & debug tools
    sessionDataset: string; // Dataset locked to the active session (read-only once set)
    availableDatasets: DatasetInfo[]; // Populated from GET /UserDocument/datasets
    analyticsMode: boolean; // When true, chat sends to /analytics endpoint instead of /chatv2
    // Mode locked to the active/loaded session; drives rendering & sending. '' when there is no active session.
    sessionMode: '' | 'analytics' | 'segmentation';
};

const persistedState = loadState();

const initialState: SettingsState = {
    apiEndpoint: 'https://wa-orchestration.azurewebsites.net',
    name: '',
    pseudocodeChat: [],
    chat: [],
    isProcessing: false,
    recentReasoningNotifications: [],
    processNotificationIndex: null,
    sessionId: '',
    sessionOwner: '',
    isViewingOthersSession: false,
    activeSessionId: '',
    context: {},
    isDialogOpen: false,
    dialogSearchString: '',
    searchTabString: 'title',
    useChatv2: true,
    enableExportFeature: false,
    enableEvaluation: false,
    evaluationFeedback: [],
    // Dataset is populated from server via GET /datasets — the one marked isDefault will be selected
    dataset: '',
    sessionDataset: '',
    availableDatasets: [],
    analyticsMode: false,
    sessionMode: '',
    ...persistedState,
};

const isUserChatElement = (
    element: ChatElement
): element is ChatSegmentRequest => {
    return element.role === 'User' && 'dimensionContext' in element;
};

/**
 * Extracts the base pattern from a message with progress indicators
 * Examples:
 *   "Analyzing generated Lua Script (1/6)" → "Analyzing generated Lua Script"
 *   "Processing data (45%)" → "Processing data"
 *   "Regular message" → null
 */
const extractMessagePattern = (message: string): string | null => {
    // Match pattern: "Text (X/Y)" or "Text (X%)"
    const progressMatch = message.match(/^(.+?)\s*\((?:\d+\/\d+|\d+%)\)$/);
    if (progressMatch) {
        return progressMatch[1].trim();
    }
    return null;
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        changeSetting: (
            state,
            action: PayloadAction<{
                key: keyof SettingsState;
                value:
                    | string
                    | ChatElement[]
                    | boolean
                    | ChatItem[]
                    | ChatElement;
            }>
        ) => {
            // @ts-ignore
            state[action.payload.key] = action.payload.value;
        },
        addPseudoMessage: (state, action: PayloadAction<ChatElement>) => {
            // First, append any pending reasoning notifications as a single SystemMessage ChatElement
            if (state.recentReasoningNotifications.length > 0) {
                // Check if the process notification was cancelled (starts with ✗)
                const processNotification = state.processNotificationIndex !== null
                    ? state.recentReasoningNotifications[state.processNotificationIndex]
                    : null;
                const isCancelled = processNotification?.startsWith('✗ ');
                
                // If cancelled, include all notifications; otherwise filter out the active process notification
                const notificationsToInclude = isCancelled
                    ? state.recentReasoningNotifications
                    : state.recentReasoningNotifications.filter(
                        (_, index) => index !== state.processNotificationIndex
                    );
                
                // Only create combined message if there are notifications to include
                if (notificationsToInclude.length > 0) {
                    const combinedReasoningMessage: ChatElement = {
                        role: 'AssistantReasoning',
                        sessionId: state.sessionId,
                        message: notificationsToInclude,
                        endpoint: 'hub',
                    };

                    // Add combined reasoning notifications to chat as a single element
                    state.chat.push(combinedReasoningMessage);
                }
                
                // Reset reasoning notifications and process notification index
                state.recentReasoningNotifications = [];
                state.processNotificationIndex = null;
            }
            
            state.chat.push(action.payload);
        },
        setPseudoMessage: (state, action: PayloadAction<ChatItem[]>) => {
            state.pseudocodeChat = action.payload;
        },
        setScriptMessage: (state, action: PayloadAction<ChatElement[]>) => {
            state.chat = action.payload;

            const findLastUserWithDimensionContext = (
                arr: ChatElement[]
            ): ChatSegmentRequest | undefined => {
                for (let i = arr.length - 1; i >= 0; i--) {
                    var item = arr[i];
                    if (item.role === 'User' && 'dimensionContext' in item) {
                        return item;
                    }
                }
                return undefined;
            };

            const lastUserWithDimensionContext =
                findLastUserWithDimensionContext(state.chat);
            if (
                lastUserWithDimensionContext &&
                lastUserWithDimensionContext.dimensionContext
            ) {
                var newContext: { [key: string]: ContextItem } = {};
                const dimensionContext: ContextItem[] = JSON.parse(
                    lastUserWithDimensionContext.dimensionContext
                );
                for (const item of dimensionContext) {
                    const key = item.DisplayName;
                    newContext[key] = item;
                    // Assuming you have a way to dispatch actions within a reducer or you may need to handle this differently
                    // dispatch(addToContext({ key, value: item }));
                }
                state.context = newContext;
            }
        },
        setProcessing: (state, action: PayloadAction<boolean>) => {
            state.isProcessing = action.payload;
            state.recentReasoningNotifications = [];
            state.processNotificationIndex = null;
        },
        markProcessAsCancelled: (state) => {
            // Mark the current process notification as cancelled
            if (state.processNotificationIndex !== null && state.processNotificationIndex < state.recentReasoningNotifications.length) {
                const currentProcessMessage = state.recentReasoningNotifications[state.processNotificationIndex];
                state.recentReasoningNotifications[state.processNotificationIndex] = `✗ ${currentProcessMessage}`;
                // Clear the process notification index so it's treated as a regular notification
                state.processNotificationIndex = null;
            }
        },
        setRecentReasoningNotifications: (state, action: PayloadAction<{ message: string; isProcessNotification?: boolean }>) => {
            const { message, isProcessNotification } = action.payload;
            
            if (isProcessNotification) {
                // Check if we have an existing process notification
                if (state.processNotificationIndex !== null && state.processNotificationIndex < state.recentReasoningNotifications.length) {
                    const currentProcessMessage = state.recentReasoningNotifications[state.processNotificationIndex];
                    
                    // Extract patterns to see if this is an update to the same process
                    const newPattern = extractMessagePattern(message);
                    const currentPattern = extractMessagePattern(currentProcessMessage);
                    
                    if (newPattern && currentPattern && newPattern === currentPattern) {
                        // SAME pattern → REPLACE the message in place (e.g., "(1/6)" → "(2/6)")
                        state.recentReasoningNotifications[state.processNotificationIndex] = message;
                        // Keep the same index - don't add a new notification
                    } else {
                        // DIFFERENT pattern → mark old as completed, add new
                        state.recentReasoningNotifications[state.processNotificationIndex] = `✓ ${currentProcessMessage}`;
                        
                        // Add the new process notification at the end and track its index
                        const newIndex = state.recentReasoningNotifications.length;
                        state.recentReasoningNotifications.push(message);
                        state.processNotificationIndex = newIndex;
                    }
                } else {
                    // No existing process notification - just add the new one
                    const newIndex = state.recentReasoningNotifications.length;
                    state.recentReasoningNotifications.push(message);
                    state.processNotificationIndex = newIndex;
                }
            } else {
                // Regular notification - just add it
                state.recentReasoningNotifications.push(message);
            }
        },
        setSessionId: (state, action: PayloadAction<string>) => {
            state.sessionId = action.payload;
        },
        setSessionOwner: (state, action: PayloadAction<string>) => {
            state.sessionOwner = action.payload;
        },
        setIsViewingOthersSession: (state, action: PayloadAction<boolean>) => {
            state.isViewingOthersSession = action.payload;
        },
        setActiveSessionId: (state, action: PayloadAction<string>) => {
            state.activeSessionId = action.payload; // Add reducer for setting activeSessionId
        },
        setDataset: (state, action: PayloadAction<string>) => {
            state.dataset = action.payload;
        },
        setSessionDataset: (state, action: PayloadAction<string>) => {
            state.sessionDataset = action.payload;
        },
        setAvailableDatasets: (state, action: PayloadAction<DatasetInfo[]>) => {
            state.availableDatasets = action.payload;
        },
        setAnalyticsMode: (state, action: PayloadAction<boolean>) => {
            state.analyticsMode = action.payload;
        },
        setSessionMode: (
            state,
            action: PayloadAction<'' | 'analytics' | 'segmentation'>
        ) => {
            state.sessionMode = action.payload;
        },
        // Action to clear the context dictionary
        clearContext: (state) => {
            state.context = {};
        },

        // Action to add an item to the context dictionary
        addToContext: (
            state,
            action: PayloadAction<{ key: string; value: ContextItem }>
        ) => {
            const { key, value } = action.payload;
            // Add timestamp to maintain insertion order
            const contextItemWithTimestamp = {
                ...value,
                addedAt: Date.now()
            };
            state.context[key] = contextItemWithTimestamp;
        },

        // Action to remove an item from the context dictionary by key
        removeFromContext: (state, action: PayloadAction<string>) => {
            const keyToRemove = action.payload;
            delete state.context[keyToRemove];
        },
        setSelectedTab: (state, action: PayloadAction<TabValue>) => {
            state.searchTabString = action.payload;
        },
        toggleDialog: (
            state,
            action: PayloadAction<{
                isOpen: boolean;
                searchString?: string;
                searchTab?: string;
            }>
        ) => {
            state.isDialogOpen = action.payload.isOpen;
            if (action.payload.isOpen) {
                // If a searchString is provided, update it, otherwise keep the existing one
                if (action.payload.searchString !== undefined) {
                    state.dialogSearchString = action.payload.searchString;
                }

                if (action.payload.searchTab !== undefined) {
                    state.searchTabString = action.payload.searchTab;
                }
            } else {
                state.dialogSearchString = '';
            }
        },
        // Optionally, if you want to be able to set the search string independently
        setDialogSearchString: (state, action: PayloadAction<string>) => {
            state.dialogSearchString = action.payload;
        },
        // Action to add evaluation feedback
        addEvaluationFeedback: (
            state,
            action: PayloadAction<{ evaluationType: 'unsure' | 'mismatch' | 'info'; message: string; sessionId: string }>
        ) => {
            const { evaluationType, message, sessionId } = action.payload;
            state.evaluationFeedback.push({
                evaluationType,
                message,
                sessionId,
                timestamp: Date.now(),
            });
        },
        // Action to remove evaluation feedback by timestamp
        removeEvaluationFeedback: (
            state,
            action: PayloadAction<number>
        ) => {
            state.evaluationFeedback = state.evaluationFeedback.filter(
                feedback => feedback.timestamp !== action.payload
            );
        },
    },
});

export const {
    changeSetting,
    addPseudoMessage,
    setPseudoMessage,
    setScriptMessage,
    setProcessing,
    setRecentReasoningNotifications,
    markProcessAsCancelled,
    setSessionId,
    setSessionOwner,
    setIsViewingOthersSession,
    setActiveSessionId,
    setDataset,
    setSessionDataset,
    setAvailableDatasets,
    setAnalyticsMode,
    setSessionMode,
    clearContext,
    addToContext,
    removeFromContext,
    toggleDialog,
    setDialogSearchString,
    setSelectedTab,
    addEvaluationFeedback,
    removeEvaluationFeedback,
} = settingsSlice.actions;

export const store = configureStore({
    reducer: {
        settings: settingsSlice.reducer,
    },
});

store.subscribe(() => {
    const state = store.getState().settings;
    saveState({
        apiEndpoint: state.apiEndpoint,
        name: state.name,
        enableExportFeature: state.enableExportFeature,
        enableEvaluation: state.enableEvaluation,
        dataset: state.dataset,
        analyticsMode: state.analyticsMode,
    });
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Effective analytics flag: an active session's locked mode wins; otherwise fall back to the global toggle.
export const selectEffectiveAnalytics = (settings: SettingsState): boolean =>
    settings.sessionMode ? settings.sessionMode === 'analytics' : settings.analyticsMode;

export {};
